import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTenantStore } from '@/store/tenantStore';
import type { LoginState } from '@/hooks/useLogin';
import { useLocalization } from '@/hooks/useLocalization';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { translateDynamicContent } from '@/i18n';
import { analyticsService } from '@/services/analytics/analyticsService';
import { showToast } from '@/utils/globalToast';
import { validateEmail, validatePassword } from '@/utils/formValidators';
import { ROUTES } from '@/app/router/routes';
import { AuthLogo, useShowsAuthLogo } from './AuthLogo';
import { AuthTextField, PasswordToggle, authControlStyles as controls } from './AuthControls';
import styles from './SignInForm.module.css';

const EMAIL_ERROR_ID = 'sign-in-email-error';
const PASSWORD_ERROR_ID = 'sign-in-password-error';

/**
 * SignInForm — email/password sign in, the form column of /login.
 *
 * Mirrors Flutter WebLoginForms (presentation/web/auth/web_login/web_login_forms.dart) + the
 * WebLoginBloc._OnLoginWithEmail it triggers, with the WebLoginPageConstants texts and
 * FormValidators.validateEmail / validatePassword:
 *   - desktop: the auth logo top right (or a 20.h gap without it), the tenant preference
 *     loginFormWelcomeText, "Sign In", Email ID, Password, "Forgot Password ?", Login, and
 *     "Don't have an account ? Create your account" (→ /signUp with the same return destination)
 *   - a failed login shows Flutter's message as a toast (CustomFlushBar)
 * Google / Apple / phone login are commented out in Flutter web, so they are not offered.
 * Besides on Login (as in Flutter), a field that was edited is checked when it loses focus, the
 * same way as on the sign-up form.
 */

interface SignInFormProps {
  login: LoginState;
  onForgotPassword: (email: string) => void;
}

export function SignInForm({ login, onForgotPassword }: SignInFormProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { preferences } = useTenantStore();
  const { t } = useLocalization();
  const isMobile = useIsMobile();
  const showsAuthLogo = useShowsAuthLogo();
  const { isLoading, error, handleEmailLogin, clearError } = login;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const passwordRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  /** Fields edited since the page opened — only those are checked on blur */
  const dirty = useRef({ email: false, password: false });

  // Flutter CustomFlushBar.failureOrSuccessFlushBar(message: state.showMessage)
  useEffect(() => {
    if (!error) return;
    showToast(error, 'error');
    clearError();
  }, [error, clearError]);

  // Flutter (desktop only): localizationDynamicContent.getValue(tenantPreferences['loginFormWelcomeText'])
  const loginFormWelcomeText = (preferences as Record<string, unknown> | null)?.['loginFormWelcomeText'] as
    | string
    | undefined;
  const loginWelcomeText = loginFormWelcomeText != null && !isMobile
    ? translateDynamicContent(loginFormWelcomeText)
    : undefined;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    clearError();

    // Flutter validates the trimmed values; the bloc then lower-cases the email
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();

    const eErr = validateEmail(trimmedEmail, t);
    const pErr = validatePassword(trimmedPassword, t);
    setEmailError(eErr);
    setPasswordError(pErr);

    if (eErr) emailRef.current?.focus();
    else if (pErr) passwordRef.current?.focus();
    if (eErr || pErr) return;
    handleEmailLogin(trimmedEmail.toLowerCase(), trimmedPassword);
  }

  // navigateTo(CoreRoute.signUpRoute, arguments: state.profileInput)
  function openSignUp() {
    void analyticsService.logScreenView({ screenName: ROUTES.SIGN_UP });
    navigate(ROUTES.SIGN_UP, { state: location.state });
  }

  return (
    <div className={styles.wrapper}>
      <AuthLogo className={styles.logo} />
      {!isMobile && !showsAuthLogo && <div className={styles.noLogoGap} aria-hidden />}

      {loginWelcomeText && <p className={styles.welcome}>{loginWelcomeText}</p>}

      <div className={styles.inner}>
        <h1 className={styles.title}>{t('Sign In')}</h1>

        <form className={styles.form} onSubmit={handleSubmit} autoComplete="on" noValidate>
          <AuthTextField
            id="sign-in-email"
            inputRef={emailRef}
            label={t('Email ID')}
            required
            type="email"
            name="email"
            autoComplete="username"
            autoFocus={!isMobile}
            placeholder={t('Enter your Email Id')}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              dirty.current.email = true;
              if (emailError) setEmailError('');
            }}
            onBlur={() => {
              if (dirty.current.email) setEmailError(validateEmail(email.trim(), t));
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                passwordRef.current?.focus();
              }
            }}
            error={emailError}
            errorId={EMAIL_ERROR_ID}
            disabled={isLoading}
          />

          <div className={styles.passwordGap} />

          <AuthTextField
            id="sign-in-password"
            inputRef={passwordRef}
            label={t('Password')}
            required
            type={showPassword ? 'text' : 'password'}
            name="password"
            autoComplete="current-password"
            placeholder={t('Enter your Password')}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              dirty.current.password = true;
              if (passwordError) setPasswordError('');
            }}
            onBlur={() => {
              if (dirty.current.password) setPasswordError(validatePassword(password.trim(), t));
            }}
            endAdornment={<PasswordToggle shown={showPassword} onToggle={() => setShowPassword(!showPassword)} />}
            error={passwordError}
            errorId={PASSWORD_ERROR_ID}
            disabled={isLoading}
          />

          <div className={styles.forgotRow}>
            <button type="button" className={styles.forgotLink} onClick={() => onForgotPassword(email.trim())}>
              {t('Forgot Password ?')}
            </button>
          </div>

          <button type="submit" className={`${controls.customButton} ${styles.loginBtn}`} disabled={isLoading}>
            {t('Login')}
          </button>
        </form>
      </div>

      <p className={styles.switchRow}>
        <button type="button" className={styles.switchLink} onClick={openSignUp}>
          {t("Don't have an account ?")}
          <strong className={styles.switchAction}>{` ${t('Create your account')}`}</strong>
        </button>
      </p>
    </div>
  );
}
